import React from 'react';
import { useTranslation } from 'react-i18next';
import LocalizedLink from '../components/LocalizedLink.js';
import Seo from '../components/Seo.js';

// Wrapped gift box with a bow; `ribbon`/`box` are Tailwind text colors
// applied via currentColor so each gift can be tinted differently.
const GiftIcon = ({ className, box, ribbon }) => (
  <svg className={className} viewBox="0 0 64 64" aria-hidden="true">
    <g className={box}>
      <rect x="8" y="26" width="48" height="32" rx="3" fill="currentColor" />
      <rect x="4" y="18" width="56" height="12" rx="3" fill="currentColor" opacity="0.85" />
    </g>
    <g className={ribbon}>
      <rect x="28" y="18" width="8" height="40" fill="currentColor" />
      <path d="M32 18c-4-10-16-12-16-4 0 5 9 5 16 4z" fill="currentColor" />
      <path d="M32 18c4-10 16-12 16-4 0 5-9 5-16 4z" fill="currentColor" />
    </g>
  </svg>
);

const SUGGESTED_LINKS = [
  { to: '/', key: 'home' },
  { to: '/signup', key: 'signup' },
  { to: '/login', key: 'login' },
  { to: '/privacy-policy', key: 'privacyPolicy' },
  { to: '/terms-of-use', key: 'termsOfUse' },
];

function NotFound() {
  const { t } = useTranslation();

  return (
    <section className="relative overflow-hidden py-20 sm:py-28 bg-red-50 festive-bg-pattern">
      <Seo title={t('notFound.metaTitle')} description={t('notFound.message')} noindex />

      {/* Scattered decorative gifts */}
      <GiftIcon className="hidden sm:block absolute top-10 left-[8%] w-16 h-16 -rotate-12 opacity-80" box="text-green-700" ribbon="text-red-500" />
      <GiftIcon className="hidden sm:block absolute bottom-12 left-[14%] w-12 h-12 rotate-6 opacity-70" box="text-red-600" ribbon="text-yellow-400" />
      <GiftIcon className="hidden sm:block absolute top-16 right-[10%] w-14 h-14 rotate-12 opacity-80" box="text-blue-600" ribbon="text-yellow-300" />
      <GiftIcon className="hidden sm:block absolute bottom-10 right-[16%] w-16 h-16 -rotate-6 opacity-70" box="text-green-600" ribbon="text-white" />

      <div className="relative max-w-3xl mx-auto px-6 text-center">
        <div className="bg-white/90 rounded-2xl shadow-xl px-6 py-12 sm:px-12">
          <GiftIcon className="w-28 h-28 mx-auto mb-4" box="text-red-600" ribbon="text-green-700" />
          <p className="text-6xl sm:text-7xl font-black text-red-600 tracking-tight mb-2">404</p>
          <h1 className="text-3xl sm:text-4xl font-extrabold text-gray-900 mb-4">{t('notFound.title')}</h1>
          <p className="text-lg text-gray-600 mb-8 max-w-xl mx-auto">{t('notFound.message')}</p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <LocalizedLink
              to="/"
              className="cta-button inline-flex items-center justify-center px-8 py-3 text-lg font-bold rounded-full text-white bg-green-700 hover:bg-green-800"
            >
              {t('notFound.backHome')}
            </LocalizedLink>
            <LocalizedLink
              to="/signup"
              className="cta-button inline-flex items-center justify-center px-8 py-3 text-lg font-bold rounded-full text-red-600 bg-white border-2 border-red-600 hover:bg-red-50"
            >
              {t('notFound.startExchange')}
            </LocalizedLink>
          </div>
        </div>

        <nav className="mt-10" aria-label={t('notFound.suggestionsTitle')}>
          <h2 className="text-xl font-bold text-red-700 mb-4">{t('notFound.suggestionsTitle')}</h2>
          <ul className="flex flex-wrap justify-center gap-3">
            {SUGGESTED_LINKS.map(({ to, key }) => (
              <li key={key}>
                <LocalizedLink
                  to={to}
                  className="inline-block px-4 py-2 text-sm font-medium rounded-full bg-white text-gray-700 shadow hover:text-red-600 hover:shadow-md transition"
                >
                  {t(`notFound.links.${key}`)}
                </LocalizedLink>
              </li>
            ))}
          </ul>
        </nav>
      </div>
    </section>
  );
}

export default NotFound;
